import type { PartnerNoti } from '@seek/indie-api-types';
import {
  Column,
  Columns,
  IconAdd,
  Loader,
  Stack,
  Text,
  TextLinkButton,
  useToast,
} from 'braid-design-system';
import { useState } from 'react';

import { useNotiConfig } from '../../../../ConfigContext.tsx';

import { ItemDialog } from './components/ItemRow/ItemDialog.tsx';
import { ItemRow } from './components/ItemRow/ItemRow.tsx';

interface MultiItemConfigProps {
  itemKey: 'emailAddresses' | 'phoneNumbers';
  newFormTitle: string;
}
export type EnabledSource = {
  source: PartnerNoti.SourceType;
  enabled: boolean;
};

export type EmailAddress = {
  email: string;
  enabledSources: EnabledSource[];
};

export type PhoneNumber = {
  number: string;
  enabledSources: EnabledSource[];
};

export type Item = PhoneNumber | EmailAddress;

export const MultiItemConfig = ({
  itemKey,
  newFormTitle,
}: MultiItemConfigProps) => {
  const [showAddForm, setShowAddForm] = useState(false);

  const showToast = useToast();

  const { notiConfig, loading, update } = useNotiConfig();

  const removeItem = async (itemToRemove: Item) => {
    const newItemList = notiConfig[itemKey]?.filter(
      (item) => item !== itemToRemove,
    );

    const updatedConfig = {
      ...notiConfig,
      enableSyndicationFeeds: Boolean(notiConfig.feedUrlSecret),
      [itemKey]: newItemList,
    };

    const error = await update(updatedConfig);

    if (error) {
      showToast({
        tone: 'critical',
        message: `We couldn’t remove ${
          'email' in itemToRemove ? itemToRemove.email : itemToRemove.number
        } from the list.`,
        description: String(error),
      });
    }
  };

  if (loading) {
    return <Loader />;
  }

  const itemRows = notiConfig[itemKey]?.map((item, index) => (
    <ItemRow
      key={`${itemKey}-row-${index}`}
      item={item}
      removeItem={removeItem}
      itemKey={itemKey}
      notiConfig={notiConfig}
    />
  ));

  return (
    <>
      <Stack space="medium">
        {itemRows.length > 0 ? (
          <Columns space="small">
            <Column width="content">
              <Stack space="medium">{itemRows}</Stack>
            </Column>
          </Columns>
        ) : null}

        <Text>
          <TextLinkButton
            icon={<IconAdd />}
            onClick={() => setShowAddForm(true)}
          >
            Add
          </TextLinkButton>
        </Text>
      </Stack>

      {showAddForm && (
        <ItemDialog
          action="add"
          selectedItem={null}
          newFormTitle={newFormTitle}
          setShowItemDialog={setShowAddForm}
          showItemDialog={showAddForm}
          itemKey={itemKey}
          notiConfig={notiConfig}
        />
      )}
    </>
  );
};
